import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { POST_QUERY_KEY } from './useGetPost';
import { COMMUNITY_POSTS_QUERY_KEY, GROUP_POSTS_QUERY_KEY } from './useGetPosts';

export type ApiResponse_Delete_Post = {
  id: number;
  title: string;
  community_id?: number;
  group_negotiation_id?: number;
};

const METHOD = 'DELETE';
const ENDPOINT = 'posts/{postId}';

export const useDeletePost = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { postId: number; communityId?: number; groupId?: number }) => {
      try {
        const { postId } = params;
        const data = await handleApiCall<ApiResponse_Delete_Post, Record<string, never>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            postId: postId.toString(),
          }),
          {}
        );

        enqueueSnackbar('Post deleted successfully!', { variant: 'success' });
        return data;
      } catch (err) {
        enqueueSnackbar('Failed to delete post.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      // Remove the specific post from cache
      queryClient.removeQueries({
        queryKey: POST_QUERY_KEY(data.id),
      });
      
      // Invalidate community posts if specified
      if (variables.communityId) {
        // Invalidate the first page directly
        queryClient.invalidateQueries({
          queryKey: COMMUNITY_POSTS_QUERY_KEY({
            communityId: variables.communityId,
            page: 1,
            limit: 20,
          }),
        });
        
        // Also invalidate all other pages
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/community/${variables.communityId}`);
            }
            return false;
          },
        });
        
        // Invalidate the community query to update counts
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1] === `communities/${variables.communityId}`;
            }
            return false;
          },
        });
      }
      
      // Invalidate group posts if specified
      if (variables.groupId) {
        // Invalidate the first page directly
        queryClient.invalidateQueries({
          queryKey: GROUP_POSTS_QUERY_KEY({
            groupId: variables.groupId,
            page: 1,
            limit: 20,
          }),
        });
        
        // Also invalidate all other pages
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/group/${variables.groupId}`);
            }
            return false;
          },
        });
      }
    },
  });
};